"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageCanvas = void 0;
exports.pageDocumentCanvas = pageDocumentCanvas;
const fractional_indexing_1 = require("./fractional-indexing");
const page_document_maps_1 = require("./page-document-maps");
const page_lookup_1 = require("./page-lookup");
const page_read_source_1 = require("./page-read-source");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_writer_1 = require("./page-structure-writer");
/** A surface and the fields in its explicitly supplied loaded parts. */
class PageCanvas {
    constructor(_doc, surfaceId, docs, index) {
        this.surfaceId = surfaceId;
        this.docs = docs;
        this.writer = new page_structure_writer_1.PageStructureWriter(docs, index);
    }
    tree() {
        return this.writer.tree();
    }
    /** Element ids of the surface in layer order. */
    ids() {
        return this.tree().elements.get(this.surfaceId) ?? [];
    }
    /** Fields of an element on the surface. */
    get(id) {
        const entry = this.writer.entries().get(id);
        return entry && entry.p === this.surfaceId
            ? this.docs.partElements(entry.s)?.get(id)
            : undefined;
    }
    /** Every element of the surface in layer order, with its fields. */
    forEach(callback) {
        for (const id of this.ids()) {
            const fields = this.get(id);
            if (fields)
                callback(fields, id);
        }
    }
    has(id) {
        return this.get(id) !== undefined;
    }
    /** Layer index of an element or block. */
    order(id) {
        return this.writer.entries().get(id)?.o;
    }
    /** Members of a group or frame. */
    members(groupId) {
        return this.tree().members.get(groupId) ?? [];
    }
    /** A layer index above every element of the surface. */
    nextOrder() {
        const top = this.ids().at(-1);
        return (0, fractional_indexing_1.generateFractionalIndexingKeyBetween)(top === undefined ? null : this.order(top), null);
    }
    /** Stores an element's fields and places it on the surface. */
    set(id, fields, order, partId) {
        const elements = this.docs.partElements(partId);
        if (!elements)
            throw new page_structure_writer_1.PageStructureWriteError(`Page part ${partId} is not loaded`);
        elements.set(id, fields);
        this.writer.placeElement({
            id,
            surfaceId: this.surfaceId,
            order,
            partId,
        });
    }
    setOrder(id, order) {
        this.writer.setOrder(id, order);
    }
    /** Makes exactly `members` the members of a group or frame. */
    setMembers(groupId, members) {
        const wanted = new Set(members);
        for (const member of this.members(groupId)) {
            if (!wanted.has(member))
                this.writer.setGroup(member, undefined);
        }
        for (const member of wanted) {
            if (this.writer.entries().has(member)) {
                this.writer.setGroup(member, groupId);
            }
        }
    }
    /** Removes an element or block with its entry and fields. */
    remove(id) {
        this.writer.removeBlock(id);
    }
}
exports.PageCanvas = PageCanvas;
/**
 * The canvas of a page and its loaded parts: its first placed surface
 * block in reading order, or null when the page has none.
 */
function pageDocumentCanvas(doc, parts = new Map()) {
    const surfaceId = (0, page_lookup_1.pageBlockIdsOfKind)((0, page_read_source_1.pageReadSource)(doc, parts), page_structure_entry_1.PAGE_SURFACE_FLAVOUR)[0];
    return surfaceId === undefined
        ? null
        : new PageCanvas(doc, surfaceId, {
            ...(0, page_structure_writer_1.pageDocumentStructure)(doc),
            loadedPartIds: () => [page_document_maps_1.PAGE_DOCUMENT_PART_ID, ...parts.keys()],
            partStructure: id => id === page_document_maps_1.PAGE_DOCUMENT_PART_ID
                ? (0, page_document_maps_1.pageStructureMap)(doc)
                : parts.has(id)
                    ? (0, page_document_maps_1.pageStructureMap)(parts.get(id))
                    : null,
            partBlocks: id => id === page_document_maps_1.PAGE_DOCUMENT_PART_ID
                ? (0, page_document_maps_1.pagePartBlocksMap)(doc)
                : parts.has(id)
                    ? (0, page_document_maps_1.pagePartBlocksMap)(parts.get(id))
                    : null,
            partElements: id => id === page_document_maps_1.PAGE_DOCUMENT_PART_ID
                ? (0, page_document_maps_1.pagePartElementsMap)(doc)
                : parts.has(id)
                    ? (0, page_document_maps_1.pagePartElementsMap)(parts.get(id))
                    : null,
        });
}
